import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FullSeriesComponent,
  ImagingGalleryItem,
  IMAGING_MISSING,
  ImageTrayComponent,
  ImagePreviewContainerComponent,
} from '@libs/imaging';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import {
  CollaborationFullSeriesPreviewDialogResult,
  CollaborationFullSeriesPreviewDialogVM,
} from './collaboration-full-series-preview-dialog.interface';

@Component({
  selector: 'lib-collaboration-full-series-preview-dialog',
  imports: [CommonModule, FullSeriesComponent, SvgIconComponent, TranslateModule, ImageTrayComponent, ImagePreviewContainerComponent],
  templateUrl: './collaboration-full-series-preview-dialog.component.html',
  styleUrl: './collaboration-full-series-preview-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollaborationFullSeriesPreviewDialogComponent {
  protected readonly dialogData: CollaborationFullSeriesPreviewDialogVM = inject(DIALOG_DATA);
  protected readonly dialogRef: DialogRef<CollaborationFullSeriesPreviewDialogResult> = inject(DialogRef);

  @ViewChild(FullSeriesComponent) public fullSeriesComponent!: FullSeriesComponent;

  protected readonly gallery = signal<ImagingGalleryItem[]>([]);
  protected readonly inDownloadMode = signal(this.dialogData.downloadMode);
  protected readonly inPreviewMode = signal(false);
  protected readonly inEditMode = signal(false);
  protected readonly inCompareMode = signal(false);
  protected readonly selectedImage = signal<ImagingGalleryItem | null>(null);
  protected readonly filteredImages = computed(() => {
    return this.gallery().filter((image) => image.id !== this.selectedImage()?.id && image.id !== this.comparisonImage()?.id);
  });
  protected readonly comparisonImage = signal<ImagingGalleryItem | null>(null);
  protected selectedImages: Signal<ImagingGalleryItem[]> = computed(() => {
    return this.comparisonImage() ? [this.selectedImage()!, this.comparisonImage()!] : [this.selectedImage()!];
  });
  protected selectedImageIds: Signal<string[]> = computed(() => this.fullSeriesComponent?.selectedImageIdsSignal() ?? []);

  protected onCloseClick(): void {
    this.dialogRef.close();
  }

  protected onAddToSelectionClick(): void {
    if (this.dialogData.previewMode && !this.inDownloadMode()) {
      this.inDownloadMode.set(true);
      return;
    }

    const selectedImages = this.fullSeriesComponent
      .selectedImageIdsSignal()
      .filter((attachment) => !this.dialogData.selectedImages?.find((selected) => selected.id === attachment));

    const deselectedImages =
      this.dialogData.selectedImages
        ?.filter((attachment) => !this.fullSeriesComponent.selectedImageIdsSignal().includes(attachment.id))
        .map((deselectedAttachment) => deselectedAttachment.id) ?? [];

    this.dialogRef.close({ selectedImages, deselectedImages, setDownloadMode: this.inDownloadMode() });
  }

  protected onImageClick(image: ImagingGalleryItem): void {
    if (image.id === IMAGING_MISSING || this.inDownloadMode()) {
      return;
    }

    if (image.groupLength > 0 && !!image.groupImages) {
      this.gallery.set(image.groupImages);
    }

    if (!this.inCompareMode()) {
      this.selectedImage.set(image.groupImages?.[0] ?? image);
    }

    this.inPreviewMode.set(true);
  }

  protected goToEditMode(editMode: boolean): void {
    this.inEditMode.set(editMode);
  }

  protected toggleCompareMode(): void {
    this.inCompareMode.update((prev) => {
      return !prev;
    });
  }

  protected setComparisonImage(image: ImagingGalleryItem | null): void {
    this.comparisonImage.set(image);
  }

  protected goBack(): void {
    if (this.inEditMode()) {
      this.inEditMode.set(false);
    } else {
      this.inPreviewMode.set(false);
    }
  }
}
